import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputSize, InputVariant } from '../../../types/ui.types';
import { cn } from '../../../utils/class-utils';

@Component({
  selector: 'jjames-input',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JJamesInputComponent),
      multi: true
    }
  ],
  template: `
    <div class="space-y-1">
      <!-- Label -->
      @if (label) {
        <label [for]="inputId" class="block text-sm font-medium text-gray-700">
          {{ label }}
          @if (required) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <!-- Input wrapper -->
      <div class="relative">
        <!-- Left icon -->
        @if (iconLeft) {
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i [class]="iconLeft + ' text-gray-400'"></i>
          </div>
        }
        
        <!-- Input -->
        <input
          [id]="inputId"
          [type]="type"
          [class]="inputClasses"
          [placeholder]="placeholder"
          [disabled]="disabled"
          [readonly]="readonly"
          [value]="value"
          (input)="onInput($event)"
          (blur)="onBlur()"
          (focus)="onFocus()"
        />
        
        <!-- Right icon -->
        @if (iconRight) {
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <i [class]="iconRight + ' text-gray-400'"></i>
          </div>
        }
      </div>
      
      <!-- Help text -->
      @if (helpText) {
        <p class="text-sm text-gray-500">{{ helpText }}</p>
      }
      
      <!-- Error message -->
      @if (errorMessage) {
        <p class="text-sm text-red-600">{{ errorMessage }}</p>
      }
    </div>
  `,
  styles: []
})
export class JJamesInputComponent implements ControlValueAccessor {
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'checkbox' = 'text';
  @Input() size: InputSize = 'md';
  @Input() variant: InputVariant = 'default';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() iconLeft?: string;
  @Input() iconRight?: string;
  @Input() helpText?: string;
  @Input() errorMessage?: string;
  @Input() inputId = `jjames-input-${Math.random().toString(36).substr(2, 9)}`;

  @Output() focused = new EventEmitter<void>();
  @Output() blurred = new EventEmitter<void>();

  value = '';
  private onChange = (value: string) => { };
  private onTouched = () => { };

  get inputClasses(): string {
    const baseClasses = 'block w-full border rounded-md shadow-sm focus:ring-1 focus:outline-none transition-colors';
    const sizeClasses = this.getSizeClasses();
    const variantClasses = this.getVariantClasses();
    const iconClasses = this.getIconClasses();
    const disabledClasses = this.disabled ? 'bg-gray-50 cursor-not-allowed' : '';

    return cn(baseClasses, sizeClasses, variantClasses, iconClasses, disabledClasses);
  }

  private getSizeClasses(): string {
    const sizes: Record<InputSize, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-2.5 text-base'
    };
    return sizes[this.size];
  }

  private getVariantClasses(): string {
    const variants: Record<InputVariant, string> = {
      default: 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      error: 'border-red-300 focus:border-red-500 focus:ring-red-500',
      success: 'border-green-300 focus:border-green-500 focus:ring-green-500'
    };
    return variants[this.variant];
  }

  private getIconClasses(): string {
    let classes = '';
    if (this.iconLeft) classes += ' pl-10';
    if (this.iconRight) classes += ' pr-10';
    return classes;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
  }

  onFocus(): void {
    this.focused.emit();
  }

  onBlur(): void {
    this.onTouched();
    this.blurred.emit();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}